import { createContext, useState, useEffect } from "react";
import CustomerService from "../services/CustomerService";
import CustomerHelper from "helpers/CustomerHelper";
import { SALE_SYNC_STATUS, COLLECTIONS, CUSTOMER_JOURNAL_TYPES, EVENTS } from "const";
import { db } from "helpers/DBHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import toast from "react-hot-toast";
import { eventsEmitter } from "utils/events";

const CustomerContext = createContext();

export const CustomerProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleCustomerUpdated = (updatedCustomer) => {
      if (!updatedCustomer) return;
      setCustomers((prev) => {
        const idx = prev.findIndex(
          (c) =>
            (updatedCustomer.local_id && c.local_id === updatedCustomer.local_id) ||
            (updatedCustomer.id && c.id === updatedCustomer.id),
        );
        if (idx !== -1) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...updatedCustomer };
          return next;
        }
        return [...prev, updatedCustomer];
      });
    };

    const unsub = eventsEmitter.on(EVENTS.CUSTOMER_UPDATED, handleCustomerUpdated);
    return unsub;
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      await CustomerHelper.syncPendingCustomers();
      const { customers, customerGroups } = await CustomerHelper.loadInitialCustomers();
      setCustomers(customers);
      setCustomerGroups(customerGroups);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateCustomerInState = (local_id, updatedCustomer) => {
    setCustomers((prev) => prev.map((c) => (c.local_id === local_id ? updatedCustomer : c)));
  };

  const addCustomer = async (data) => {
    try {
      if (data?.mobile) {
        const cleanedMobile = String(data.mobile).trim();
        const existing = customers.find((c) => String(c.mobile || "").trim() === cleanedMobile);
        if (existing) {
          const errorMsg = `Customer with mobile number '${cleanedMobile}' already exists (${existing.name}).`;
          toast.error(errorMsg);
          throw new Error(errorMsg);
        }
      }

      // 1. Save locally first to be offline-safe and get a local_id
      const local_id = await CustomerHelper.addCustomerToDB({ ...data, sync_status: SALE_SYNC_STATUS.PENDING });
      const newCustomer = { ...data, local_id };

      // Update state immediately for UI responsiveness
      setCustomers((prev) => [...prev, newCustomer]);

      // 2. Attempt to sync with backend
      try {
        const synced = await CustomerService.createCustomer(data);
        if (synced && synced.id) {
          const finalCustomer = { ...synced, local_id: local_id };
          await CustomerHelper.updateCustomerInDB(finalCustomer);

          // Update state with final data from backend
          updateCustomerInState(local_id, finalCustomer);
          return finalCustomer;
        }
      } catch (syncErr) {
        console.warn("Offline: Customer created locally but failed to sync with backend.", syncErr);
      }

      return newCustomer;
    } catch (err) {
      console.error("Failed to add customer:", err);
      throw err;
    }
  };

  const getCustomerFromLocalById = async (id) => {
    if (!id) return;
    return customers.find((c) => c.id === id);
  };

  const getCustomerFromLocalByLocalId = async (localId) => {
    if (!localId) return;
    return customers.find((c) => c.local_id === localId);
  };

  const getCustomerFromLocalByAnyId = async (id, localId) => {
    return getCustomerFromLocalById(id) || getCustomerFromLocalByLocalId(localId);
  };

  const updateCustomer = async (id, data) => {
    try {
      const customer = customers.find((c) => String(c.id) === String(id));
      const local_id = customer?.local_id;

      if (local_id) {
        const toUpdate = { ...customer, ...data };
        await CustomerHelper.updateCustomerInDB(toUpdate);
        updateCustomerInState(local_id, toUpdate);
      }

      const updated = await CustomerService.updateCustomer(id, data);

      if (updated && local_id) {
        const finalUpdate = { ...updated, local_id };
        await CustomerHelper.updateCustomerInDB(finalUpdate);
        updateCustomerInState(local_id, finalUpdate);
        return finalUpdate;
      }

      return updated;
    } catch (err) {
      console.error("Failed to update customer:", err);
      throw err;
    }
  };

  const deleteCustomer = async (id) => {
    try {
      await CustomerService.deleteCustomer(id);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("Failed to delete customer:", err);
      throw err;
    }
  };

  const applyCustomerCredit = async (customerId, amountUsed) => {
    try {
      const customer = customers.find((c) => Number(c.id) === Number(customerId));
      if (!customer) throw new Error("Customer not found");

      const updatedData = CustomerHelper.getUpdatedCustomerBalance(customer, amountUsed);

      const updatedCustomer = await CustomerService.updateCustomer(customerId, {
        balance: updatedData.balance,
      });

      if (updatedCustomer && customer.local_id) {
        const finalCustomer = { ...updatedCustomer, local_id: customer.local_id };
        await CustomerHelper.updateCustomerInDB(finalCustomer);
        updateCustomerInState(customer.local_id, finalCustomer);
        return finalCustomer;
      }

      return updatedCustomer;
    } catch (err) {
      console.error("Credit sync failed:", err);
      throw err;
    }
  };

  const updateCustomerAfterSale = async (sale, syncStatus, originalSale) => {
    if (!sale?.customer) return;

    const previousCreditUsed = Number(originalSale?.customer_credit_used || 0);
    const previousPointsUsed = Number(originalSale?.loyalty_points_used || 0);

    const totalCreditUsed = Number(sale?.customer_credit_used || 0);
    const totalPointsUsed = Number(sale?.loyalty_points_used || 0);

    const creditDifference = totalCreditUsed - Number(previousCreditUsed || 0);
    const pointsDifference = totalPointsUsed - Number(previousPointsUsed || 0);

    if (totalCreditUsed === 0 && totalPointsUsed === 0 && creditDifference === 0 && pointsDifference === 0) {
      return;
    }

    const customerId = sale.customer.id;
    const localCustomer = await getCustomerFromLocalByAnyId(customerId, sale.customer.local_id);
    let customer = null;
    let fetchedFromBackend = false;

    if (syncStatus === SALE_SYNC_STATUS.SYNCED_TO_CLOUD && customerId) {
      try {
        customer = await CustomerService.getCustomer(customerId);
        if (customer) {
          customer.local_id = localCustomer?.local_id;
          fetchedFromBackend = true;
        }
      } catch (err) {
        console.log("Failed to fetch customer from BE", err);
      }
    }

    // Reading customer from local
    if (!customer) {
      customer = localCustomer;
    }

    // Skipping local customer update if customer is not found
    if (!customer) {
      console.error("Failed to fetch customer from local");
      return;
    }

    try {
      if (!fetchedFromBackend) {
        customer.balance = (Number(customer.balance) || 0) - creditDifference;
        customer.points = (Number(customer.points) || 0) - pointsDifference;
      }
      await CustomerHelper.updateCustomerInDB(customer);
      updateCustomerInState(customer.local_id, customer);
    } catch (err) {
      console.error("Failed to update customer", err);
    }
  };

  const addPaymentToCustomer = async (customerId, payload) => {
    const now = DateTimeHelpers.getLocalISOString();
    const paymentLog = {
      customer_id: Number(customerId),
      credit: Number(payload.credit || 0),
      debit: 0,
      description: payload.note || `Payment at ${DateTimeHelpers.convertDateToIsoDMY(new Date())}`,
      time: now,
      sync_status: SALE_SYNC_STATUS.PENDING,
      sync_message: "",
      type: CUSTOMER_JOURNAL_TYPES.PAYMENT,
      shift_id: payload.shift_id,
      tenders: payload.tenders || [],
    };

    try {
      await CustomerService.addPaymentToCustomer(customerId, payload);

      // Save to local logs as synced
      paymentLog.sync_status = SALE_SYNC_STATUS.SYNCED_TO_CLOUD;
      const localId = await db.add(COLLECTIONS.CUSTOMER_PAYMENT_LOGS.name, paymentLog);
      paymentLog.local_id = localId;

      return await syncCustomer(customerId);
    } catch (err) {
      console.error("Failed to add payment to customer on server, saving offline:", err);

      // Save to local logs as pending
      paymentLog.sync_status = SALE_SYNC_STATUS.PENDING;
      const localId = await db.add(COLLECTIONS.CUSTOMER_PAYMENT_LOGS.name, paymentLog);
      paymentLog.local_id = localId;

      // Update customer balance locally in IndexedDB and in state
      let existingCustomer = customers.find(
        (c) => Number(c.id) === Number(customerId) || Number(c.local_id) === Number(customerId),
      );

      if (!existingCustomer) {
        existingCustomer = await CustomerHelper.getCustomerByAnyIdFromDB(customerId, customerId);
      }

      let newBalance = Number(payload.credit || 0);
      let updatedCustomer = existingCustomer;
      if (existingCustomer) {
        newBalance = Number(existingCustomer.balance || 0) + Number(payload.credit || 0);
        updatedCustomer = {
          ...existingCustomer,
          balance: newBalance,
        };
        const savedCustomer = await CustomerHelper.saveOrUpdateCustomerOffline(updatedCustomer);
        if (savedCustomer?.local_id) {
          updateCustomerInState(savedCustomer.local_id, savedCustomer);
          updatedCustomer = savedCustomer;
        }
      }

      // Add offline payment to cached customer journals in IndexedDB
      await CustomerHelper.addOfflinePaymentToCustomerJournals(customerId, paymentLog, newBalance);

      return { offline: true, customer: updatedCustomer };
    }
  };

  const syncCustomer = async (id) => {
    try {
      const res = await CustomerService.getCustomer(id);

      if (res) {
        const existing = customers.find((c) => Number(c.id) === Number(id));

        const local_id = existing?.local_id;

        const updatedCustomer = { ...res };
        if (local_id) {
          updatedCustomer.local_id = local_id;
          await CustomerHelper.updateCustomerInDB(updatedCustomer);
          updateCustomerInState(local_id, updatedCustomer);
        } else {
          const new_local_id = await CustomerHelper.addCustomerToDB(updatedCustomer);
          updatedCustomer.local_id = new_local_id;
          setCustomers((prev) => [...prev, updatedCustomer]);
        }

        return updatedCustomer;
      }
    } catch (err) {
      console.error("Failed to sync customer:", err);
      throw err;
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  return (
    <CustomerContext.Provider
      value={{
        customers,
        customerGroups,
        loading,
        fetchCustomers,
        addCustomer,
        updateCustomer,
        syncCustomer,
        addPaymentToCustomer,
        updateCustomerAfterSale,
        deleteCustomer,
        applyCustomerCredit,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContext;
